import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { SupportedLanguages } from "@workspace/contracts/language";
import { Tabs, TabsList, TabsTrigger } from "@workspace/ui/components/tabs";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { Details } from "@/features/project/components/details";
import { Features } from "@/features/project/components/features";
import { ProjectHeader } from "@/features/project/components/header";
import { ProjectHero } from "@/features/project/components/hero";
import { ProjectImages } from "@/features/project/components/images";
import { Location } from "@/features/project/components/location";
import { Plans } from "@/features/project/components/plans";
import { QuickStats } from "@/features/project/components/quick-stats";
import { Translations } from "@/features/project/components/translations";

export const Route = createFileRoute("/_authenticated/projects/$projectId/")({
  component: RouteComponent,
  loader: async ({ params, context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.admin.projects.findById.queryOptions({ input: { id: params.projectId } }));
  },
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(orpc.admin.projects.findById.queryOptions({ input: { id: projectId } }));

  const availableLanguages = project.translations.map((x) => x.lang);

  const [language, setLanguage] = useState<SupportedLanguages>("ar");

  const title = project.translations.find((t) => t.lang === language)?.title;
  const overview = project.translations.find((t) => t.lang === language)?.overview;

  return (
    <div className="space-y-6">
      <ProjectHeader
        activeLanguage={language}
        availableLanguages={availableLanguages}
        projectId={projectId}
        project={project}
        setLanguage={setLanguage}
      />

      <ProjectHero title={title} overview={overview} {...project} />

      <QuickStats {...project} />

      {/* Tabs */}
      <Tabs defaultValue="plans" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="translations">Translations</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="distances">Distances</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
        </TabsList>

        <Plans projectId={project.id} projectTitle={title ?? project.code} plans={project.plans} />

        <Translations projectCode={project.code} projectId={projectId} setLanguage={setLanguage} translations={project.translations} />

        <Features features={project.projectFeatures} goals={project.goals} />

        <Location distances={project.projectDistances} location={project.location} />

        <Details
          project={{
            ...project,
            title: project.translations.find((t) => t.lang === language)?.title ?? "",
            overview: project.translations.find((t) => t.lang === language)?.overview ?? "",
          }}
        />

        <ProjectImages images={project.images} altText={project.altText ?? title ?? project.code} />
      </Tabs>
    </div>
  );
}
